/**
 * billing-service 엔드포인트 — gateway 경유(`/v1/organizations|subscriptions|payments|licenses`).
 *
 * web(마이페이지·초대 수락)과 console-web(기업콘솔)이 같은 API 를 본다.
 * 앱별 endpoints 레지스트리에 복사하지 말 것 — BE 라우트가 바뀌면 한쪽만 고치게 된다.
 * baseURL 은 각 앱 client 가 주입한다 — 여기엔 path 만.
 */
export const BILLING_API_VERSION = '/v1';

export const BILLING_ENDPOINTS = {
  // 조직 — 생성 엔드포인트는 없다(서버 프로비저닝 전용).
  myGroups: `${BILLING_API_VERSION}/organizations/me/organizations`,
  group: (orgId: string) => `${BILLING_API_VERSION}/organizations/${orgId}`,
  transferOwnership: (orgId: string) =>
    `${BILLING_API_VERSION}/organizations/${orgId}/ownership`,
  // 조직 수정·해산 (화면 미구현 — BE 라우트는 존재).
  // 해산은 OWNER|ADMIN 이 쓸 수 있는 실제 기능이다. 마지막 구성원 탈퇴 시엔
  // 서버가 자동 해산하므로(P6) 현재 화면에서 직접 부를 일이 없을 뿐이다.
  updateGroup: (orgId: string) => `${BILLING_API_VERSION}/organizations/${orgId}`,
  disbandGroup: (orgId: string) =>
    `${BILLING_API_VERSION}/organizations/${orgId}/disband`,
  // 멤버
  members: (orgId: string) =>
    `${BILLING_API_VERSION}/organizations/${orgId}/member`,
  updateMemberRole: (orgId: string, memberId: string) =>
    `${BILLING_API_VERSION}/organizations/${orgId}/member/${memberId}/role`,
  suspendMember: (orgId: string, memberId: string) =>
    `${BILLING_API_VERSION}/organizations/${orgId}/member/${memberId}/suspend`,
  reactivateMember: (orgId: string, memberId: string) =>
    `${BILLING_API_VERSION}/organizations/${orgId}/member/${memberId}/reactivate`,
  // 구성원 계정 생성 — 초대가 아니라 계정째로 만든다(billing 이 auth 를 호출).
  createMember: (orgId: string) =>
    `${BILLING_API_VERSION}/organizations/${orgId}/member`,
  resetMemberPassword: (orgId: string, memberId: string) =>
    `${BILLING_API_VERSION}/organizations/${orgId}/member/${memberId}/reset-password`,
  // 구성원 삭제 — org_members 하드 삭제 + auth 계정 잠금·soft-delete.
  deleteMember: (orgId: string, memberId: string) =>
    `${BILLING_API_VERSION}/organizations/${orgId}/member/${memberId}`,
  // 검증
  verifications: (orgId: string) =>
    `${BILLING_API_VERSION}/organizations/${orgId}/verification`,
  // 시트(라이선스) 현황
  seats: (orgId: string) =>
    `${BILLING_API_VERSION}/organizations/${orgId}/seats`,
  // 조직 시트(라이선스) 목록 — 시트 관리 화면(VIEW_BILLING)
  seatLicenses: (orgId: string) =>
    `${BILLING_API_VERSION}/organizations/${orgId}/seats/licenses`,
  // 조직 보유 플랜 — 수량 확장(재구매)용 (VIEW_BILLING)
  seatOwnedPlans: (orgId: string) =>
    `${BILLING_API_VERSION}/organizations/${orgId}/seats/plans`,
  // 라이선스 할당 대상(ACTIVE 멤버) 목록 — 시트 관리자(VIEW_BILLING)
  assignableMembers: (orgId: string) =>
    `${BILLING_API_VERSION}/organizations/${orgId}/assignable-members`,
  // 제품 단위 라이선스(§6) — 통합내역/구성원 배정
  licenseSummary: (orgId: string) =>
    `${BILLING_API_VERSION}/organizations/${orgId}/license-summary`,
  memberLicenses: (orgId: string, userId: string) =>
    `${BILLING_API_VERSION}/organizations/${orgId}/members/${userId}/licenses`,
  // 내 사용권(products[]) — 마이페이지 "내 라이선스"·기능 게이팅(§7.3)
  myEntitlement: `${BILLING_API_VERSION}/entitlements/me`,
  // 디바이스락 — 등록 기기·교체 횟수. 본인(마이페이지) / 조직 관리자(기업콘솔)
  myDevices: `${BILLING_API_VERSION}/licenses/me/devices`,
  myDevice: (deviceId: string) =>
    `${BILLING_API_VERSION}/licenses/me/devices/${deviceId}`,
  orgDevices: (orgId: string) =>
    `${BILLING_API_VERSION}/organizations/${orgId}/devices`,
  orgDevice: (orgId: string, deviceId: string) =>
    `${BILLING_API_VERSION}/organizations/${orgId}/devices/${deviceId}`,
  // 분실·퇴사 회수 — 교체 횟수 미차감, 사유 필수, 같은 PC 재등록 불가
  orgDeviceRevoke: (orgId: string, deviceId: string) =>
    `${BILLING_API_VERSION}/organizations/${orgId}/devices/${deviceId}/revoke`,
  // 시트 배정/회수 (화면은 saveMemberLicenses 벌크 경로를 쓴다 — BE 라우트는 존재)
  assignLicense: (licenseId: string) =>
    `${BILLING_API_VERSION}/licenses/${licenseId}/assign`,
  unassignLicense: (licenseId: string) =>
    `${BILLING_API_VERSION}/licenses/${licenseId}/unassign`,
  // 감사 로그 (화면 미구현 — BE 라우트는 존재)
  audit: (orgId: string) =>
    `${BILLING_API_VERSION}/organizations/${orgId}/audit`,
  // 구독 / 결제
  subscriptions: `${BILLING_API_VERSION}/subscriptions`,
  payments: `${BILLING_API_VERSION}/payments`,
  confirmPayment: `${BILLING_API_VERSION}/payments/confirm`,
  // 마이페이지 결제내역 (본인 한정 — 서버가 x-user-id 로 판정)
  myPayments: `${BILLING_API_VERSION}/payments/me`,
  myPaymentDetail: (id: string) => `${BILLING_API_VERSION}/payments/me/${id}`,
  // cancelMyPayment 는 제거됐다(§Phase 1 — 사용자 즉시 환불 차단, Gateway deny-list).
  // 미완료/실패 결제 이어서 결제 (본인 한정)
  resumeMyPayment: (id: string) => `${BILLING_API_VERSION}/payments/me/${id}/resume`,
  // 조직 결제내역 (기업콘솔 — VIEW_BILLING)
  orgPayments: (orgId: string) =>
    `${BILLING_API_VERSION}/organizations/${orgId}/payments`,
  orgPaymentDetail: (orgId: string, id: string) =>
    `${BILLING_API_VERSION}/organizations/${orgId}/payments/${id}`,
  resumeOrgPayment: (orgId: string, id: string) =>
    `${BILLING_API_VERSION}/organizations/${orgId}/payments/${id}/resume`,
  // 견적서 (스토어 — 본인 한정)
  quotes: `${BILLING_API_VERSION}/quotes`,
  myQuotes: `${BILLING_API_VERSION}/quotes/me`,
  myQuoteDetail: (id: string) => `${BILLING_API_VERSION}/quotes/me/${id}`,
  /**
   * 교육 접수 — billing-service 의 education 모듈.
   *
   * `/education/catalog/*` 만 비로그인 공개다(Gateway public-paths PREFIX).
   * 접수·확정은 로그인 필수. 이 prefix 구분을 흐트러뜨리면 접수가 통째로 열린다.
   */
  educationCourses: `${BILLING_API_VERSION}/education/catalog/courses`,
  educationCourse: (id: string) =>
    `${BILLING_API_VERSION}/education/catalog/courses/${id}`,
  educationSessions: `${BILLING_API_VERSION}/education/catalog/sessions`,
  educationSession: (id: string) =>
    `${BILLING_API_VERSION}/education/catalog/sessions/${id}`,
  educationRegistrations: `${BILLING_API_VERSION}/education/registrations`,
  educationMemberOptions: `${BILLING_API_VERSION}/education/registrations/member-options`,
  myEducationRegistrations: `${BILLING_API_VERSION}/education/registrations/me`,
  myEducationRegistration: (id: string) =>
    `${BILLING_API_VERSION}/education/registrations/me/${id}`,
  confirmEducationRegistration: (id: string) =>
    `${BILLING_API_VERSION}/education/registrations/${id}/confirm`,
  /** 결제창을 닫은 접수의 결제 재개. 새 접수를 만들지 않고 기존 홀드를 이어 쓴다. */
  resumeEducationRegistration: (id: string) =>
    `${BILLING_API_VERSION}/education/registrations/${id}/resume`,
  // 쿠폰 상환 (라이선스 발급 쿠폰)
  couponPreview: `${BILLING_API_VERSION}/coupons/preview`,
  couponRedeem: `${BILLING_API_VERSION}/coupons/redeem`,
  // 구독·라이선스 조회 (본인 — 서버가 x-user-id 로 판정)
  // 아바타 메뉴(모달)용 개인 구독 — ACTIVE 외 PAST_DUE·PAUSED 포함(재활성화 노출)
  myDisplaySubscription: `${BILLING_API_VERSION}/subscriptions/me/subscription`,
  myActiveLicenses: `${BILLING_API_VERSION}/licenses/me/active`,
  // 자동결제 구독 완성(카드등록 후)
  completeBillingSubscription: `${BILLING_API_VERSION}/subscriptions/billing/complete`,
  // 단품 다중 구매 — 카드등록 1회로 플랜별 구독을 함께 만든다
  completeBillingSubscriptionBatch: `${BILLING_API_VERSION}/subscriptions/billing/complete-batch`,
  // 이미 보유한 상품 id (스토어 '이용 중' 표시·중복구매 안내)
  myOwnedProducts: `${BILLING_API_VERSION}/subscriptions/me/owned-products`,
  // 구버전 이관 기간제 구독 상품 (결제 확인 화면의 기간 겹침 안내)
  myLegacyTerms: `${BILLING_API_VERSION}/subscriptions/me/legacy-terms`,
  // 결제수단(카드) 변경 / 재활성화
  changeSubscriptionCard: (id: string) =>
    `${BILLING_API_VERSION}/subscriptions/${id}/payment-method`,
  reactivateSubscription: (id: string) =>
    `${BILLING_API_VERSION}/subscriptions/${id}/reactivate`,
  // 약정 중도해지(§7.3/§7.8) — 정산 프리뷰 / 요청 접수
  earlyTerminationPreview: (id: string) =>
    `${BILLING_API_VERSION}/subscriptions/${id}/early-termination/preview`,
  cancelRequest: (id: string) =>
    `${BILLING_API_VERSION}/subscriptions/${id}/cancel-request`,
  // 해지 철회 — 주기말 해지 예약/중도해지 요청 되돌리기(확정 해지는 불가)
  revokeCancelRequest: (id: string) =>
    `${BILLING_API_VERSION}/subscriptions/${id}/cancel-request/revoke`,
  // 해지 영향도(dry-run) — 좌석 이관/회수 인원
  seatImpact: (id: string) =>
    `${BILLING_API_VERSION}/subscriptions/${id}/seat-impact`,
  // 재활성화 프리뷰 — 즉시 전액 청구라 금액을 먼저 확인시킨다
  reactivatePreview: (id: string) =>
    `${BILLING_API_VERSION}/subscriptions/${id}/reactivate/preview`,
  // 청약철회(§6.1) — 개인 구독·미사용·7일 이내에만 열린다.
  // 기업 구독과 예외 심사는 Admin 재량 경로(internal)로만 처리된다.
  withdrawalPreview: (id: string) =>
    `${BILLING_API_VERSION}/subscriptions/${id}/withdrawal/preview`,
  withdrawal: (id: string) => `${BILLING_API_VERSION}/subscriptions/${id}/withdrawal`,
  // 개인 연간 중도해지(환불·해지 정책 제5조 ④) — 환불 프리뷰 / 신청 접수(환불은 운영자 승인 시)
  annualTerminationPreview: (id: string) =>
    `${BILLING_API_VERSION}/subscriptions/${id}/annual-termination/preview`,
  annualTermination: (id: string) =>
    `${BILLING_API_VERSION}/subscriptions/${id}/annual-termination`,
  // 약정 연장(§7.4)
  extendCommitment: (id: string) =>
    `${BILLING_API_VERSION}/subscriptions/${id}/commitment/extend`,
  // 수량 모델(기업) — 좌석 추가 / 활성 빌링 구독 조회
  orgAddSeats: (orgId: string) =>
    `${BILLING_API_VERSION}/subscriptions/org/${orgId}/seats`,
  // 좌석 추가 미리보기(dry-run) — 즉시청구/다음갱신 금액
  orgSeatsPreview: (orgId: string) =>
    `${BILLING_API_VERSION}/subscriptions/org/${orgId}/seats/preview`,
  // 좌석 감소 예약(다음 갱신 시 적용) / 예약 취소
  orgDecreaseSeats: (orgId: string) =>
    `${BILLING_API_VERSION}/subscriptions/org/${orgId}/seats/decrease`,
  orgCancelSeatDecrease: (orgId: string) =>
    `${BILLING_API_VERSION}/subscriptions/org/${orgId}/seats/decrease/cancel`,
  orgBillingSubscription: (orgId: string, planId: string) =>
    `${BILLING_API_VERSION}/subscriptions/org/${orgId}/billing/${planId}`,
  // 조직 빌링 구독 목록 — 결제수단(카드) 관리용
  orgBillingSubscriptions: (orgId: string) =>
    `${BILLING_API_VERSION}/subscriptions/org/${orgId}/billing`,
  orgPurchaseBlocks: (orgId: string) =>
    `${BILLING_API_VERSION}/subscriptions/org/${orgId}/purchase-blocks`,
  // 조직 미수 정산(중도해지 위약금) 셀프 수납 — 콘솔 결제·구독 화면
  // cancelRequestId 를 주면 그 한 건만(미수가 아니어도) 돌아온다 — 결제 결과 확인용.
  orgSettlements: (orgId: string, cancelRequestId?: string) =>
    `${BILLING_API_VERSION}/subscriptions/org/${orgId}/settlements` +
    (cancelRequestId
      ? `?cancelRequestId=${encodeURIComponent(cancelRequestId)}`
      : ''),
  orgSettlementCheckout: (orgId: string, cancelRequestId: string) =>
    `${BILLING_API_VERSION}/subscriptions/org/${orgId}/settlements/${cancelRequestId}/checkout`,
  orgSettlementConfirm: (orgId: string, cancelRequestId: string) =>
    `${BILLING_API_VERSION}/subscriptions/org/${orgId}/settlements/${cancelRequestId}/confirm`,
} as const;
